import React, { useState } from "react"
import { Drawer, Input, Alert, Descriptions } from "antd"
import { useMutation } from "@tanstack/react-query"
import { Button, Label } from "components"
import { useAppNotificationStore } from "stores"
import { onQueryError } from "utils/utils"
import {
  leaveService,
  ILeavePlanningPeriod,
  PLANNING_STATUS,
} from "services/leaveService"
import { LeavePlanStatusTag } from "./leavePlanStatus"

interface RosterPeriodDrawerProps {
  period: ILeavePlanningPeriod | null
  onClose: () => void
  onChanged: () => void
}

/**
 * AL-06. The two coordination actions a supervisor has on a planned period.
 *
 * Requesting a revision keeps the period on the roster rather than removing it, so the
 * departmental picture does not appear to lose people while they revise — which is the whole
 * point of looking at the roster.
 */
const RosterPeriodDrawer: React.FC<RosterPeriodDrawerProps> = ({
  period,
  onClose,
  onChanged,
}) => {
  const [comment, setComment] = useState("")
  const { toast } = useAppNotificationStore()

  const revision = useMutation({
    mutationFn: (periodId: number) =>
      leaveService.requestPlanningRevision(periodId, comment.trim()),
    onSuccess: (res: any) => {
      toast.success(res?.message || "Changes requested.")
      setComment("")
      onChanged()
    },
    onError: onQueryError,
  })

  const coordinate = useMutation({
    mutationFn: (periodId: number) =>
      leaveService.markPlanningCoordinated(periodId),
    onSuccess: (res: any) => {
      toast.success(res?.message || "Marked as agreed.")
      setComment("")
      onChanged()
    },
    onError: onQueryError,
  })

  const busy = revision.isLoading || coordinate.isLoading

  // Settled and converted periods are no longer the department's to move: one has been agreed,
  // the other has become a real request with a balance behind it.
  const isSettled =
    period?.status_id === PLANNING_STATUS.COORDINATED ||
    period?.status_id === PLANNING_STATUS.CONVERTED

  return (
    <Drawer
      open={!!period}
      onClose={onClose}
      width={480}
      title={period?.full_name ?? "Planned period"}
      destroyOnClose
    >
      {period ? (
        <div className="flex flex-col gap-4">
          <Descriptions
            column={1}
            size="small"
            bordered
          >
            <Descriptions.Item label="Status">
              <LeavePlanStatusTag
                statusId={period.status_id}
                serverName={period.status}
                audience="supervisor"
              />
            </Descriptions.Item>
            <Descriptions.Item label="Leave type">
              {period.leave_policy ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Department">
              {period.department ?? "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Dates">
              {period.first_day} – {period.last_day}
            </Descriptions.Item>
            <Descriptions.Item label="Indicative days">
              {period.indicative_days ?? "—"}
            </Descriptions.Item>
            {period.note ? (
              <Descriptions.Item label="Employee note">
                {period.note}
              </Descriptions.Item>
            ) : null}
            {period.review_note ? (
              <Descriptions.Item label="Last comment">
                {period.review_note}
              </Descriptions.Item>
            ) : null}
          </Descriptions>

          {period.status_id === PLANNING_STATUS.CONVERTED ? (
            <Alert
              type="info"
              showIcon
              message="Booked as leave"
              description="This plan has become a leave request. It is handled through the normal approval process, and its days count against the balance."
            />
          ) : null}

          {period.status_id === PLANNING_STATUS.COORDINATED ? (
            <Alert
              type="success"
              showIcon
              message="Agreed with the department"
              description="The employee can no longer change these dates. Nothing is booked until they request the leave."
            />
          ) : null}

          {!isSettled ? (
            <>
              <div className="flex flex-col gap-2">
                <Label>Comment for the employee</Label>
                <Input.TextArea
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Explain what needs to move, and why."
                  maxLength={500}
                  showCount
                />
              </div>

              <div className="flex flex-wrap gap-3">
                <Button
                  color="neutral"
                  disabled={busy || !comment.trim()}
                  submitting={revision.isLoading}
                  onClick={() =>
                    revision.mutate(period.leave_planning_period_id)
                  }
                >
                  Request changes
                </Button>

                <Button
                  color="primary"
                  disabled={busy}
                  submitting={coordinate.isLoading}
                  onClick={() =>
                    coordinate.mutate(period.leave_planning_period_id)
                  }
                >
                  Mark as agreed
                </Button>
              </div>

              {!comment.trim() ? (
                <span className="font-avenir text-xs text-padeSubtext">
                  Add a comment to request changes, so the employee knows what
                  to move and why.
                </span>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </Drawer>
  )
}

export default RosterPeriodDrawer
